/**
 * Every word the UI uses for a system value, in one place (UX-015, UX-031).
 *
 * Rules (docs/ux/DECISIONS.md §5, docs/ux/VOICE.md):
 *   - Never render an enum, field name or camelCase key. Go through
 *     `statusLabel(kind, value)` (or the specific map) — unknown values fall
 *     back to `humanizeEnum()`, so "awaitingVerification" reads
 *     "Awaiting verification", never the raw key.
 *   - Sentence case: "In progress", not "In Progress".
 *   - Glossary: Contact (Customer / Contractor / Company), Opportunity (a Deal),
 *     Quote → Job → Invoice → Payment, Text (not SMS).
 */

// ─── glossary ───────────────────────────────────────────────────────────────

/** Canonical nouns. Import these instead of typing the word in a component. */
export const TERMS = {
  contact: 'Contact',
  contacts: 'Contacts',
  customer: 'Customer',
  customers: 'Customers',
  contractor: 'Contractor',
  contractors: 'Contractors',
  company: 'Company',
  companies: 'Companies',
  /** The UI name for a `Deal`. */
  opportunity: 'Opportunity',
  opportunities: 'Opportunities',
  lead: 'Lead',
  quote: 'Quote',
  quotes: 'Quotes',
  job: 'Job',
  jobs: 'Jobs',
  invoice: 'Invoice',
  invoices: 'Invoices',
  payment: 'Payment',
  payments: 'Payments',
  /** SMS, as a noun and a verb. */
  text: 'Text',
  texts: 'Texts',
  freeEstimate: 'Free estimate',
  crew: 'Crew',
} as const

/** Create-action labels shared by every "+" menu, quick action and empty state. */
export const CREATE_LABELS = {
  lead: 'New lead',
  quote: 'New quote',
  job: 'Schedule job',
  invoice: 'New invoice',
  payment: 'Record payment',
  contact: 'New contact',
  text: 'New text',
  event: 'New event',
} as const

// ─── contact types ──────────────────────────────────────────────────────────

export type ContactType = 'CUSTOMER' | 'CONTRACTOR' | 'VENDOR' | 'COMPANY'

export const CONTACT_TYPE_LABELS: Record<ContactType, string> = {
  CUSTOMER: 'Customer',
  CONTRACTOR: 'Sub',
  VENDOR: 'Vendor',
  // Legacy internal-team rows. Never shown as a People chip, and never "Companies".
  COMPANY: 'Internal',
}

export const CONTACT_TYPE_DESCRIPTIONS: Record<ContactType, string> = {
  CUSTOMER: 'Homeowners and businesses you do work for.',
  CONTRACTOR: 'Subs you call on the job.',
  VENDOR: 'Vendors you buy from.',
  COMPANY: 'Login crew lives in Settings → Team.',
}

// ─── roles ──────────────────────────────────────────────────────────────────

/** Built-in role labels. Accepts ADMIN or admin casing. SALES shows as "Office", SUPPORT as "Crew". Custom roles show their own name. */
export const ROLE_LABELS: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  SALES: 'Office',
  OFFICE: 'Office',
  SUPPORT: 'Crew',
  CREW: 'Crew',
  VIEWER: 'Viewer',
  MEMBER: 'Member',
}

export const ROLE_DESCRIPTIONS: Record<string, string> = {
  ADMIN: 'Everything, including settings, team, money corrections and automations.',
  SALES: 'Leads, quotes, jobs, invoices, payments, calendar and reports. No workspace settings.',
  SUPPORT: 'Their jobs and the schedule, customer contact details, starting and finishing jobs, job notes and photos. No prices or money.',
  VIEWER: 'Read-only: the Home dashboard, call history and payment status.',
}

export function roleLabel(role: string | null | undefined): string {
  if (!role) return 'Member'
  return ROLE_LABELS[role.toUpperCase()] ?? humanizeEnum(role)
}

// ─── opportunity / work stages ──────────────────────────────────────────────

/** Opportunity stages in order (DECISIONS §5). Lost sits outside the flow. */
export const OPPORTUNITY_STAGE_ORDER = ['LEAD', 'QUALIFIED', 'QUOTED', 'SCHEDULED', 'IN_PROGRESS', 'INVOICED', 'PAID'] as const

/**
 * Unified Work stages (lib/workStage + Work/Unified/stages), named as the 7
 * glossary stages (DECISIONS §5). The in-between states the board tracks
 * read as their own status ("Approved", "Completed"), the same word search
 * and notifications use (UX-183); they still sit in Quoted / In progress.
 */
export const WORK_STAGE_LABELS: Record<string, string> = {
  LEAD: 'Lead',
  QUALIFIED: 'Qualified',
  QUOTED: 'Quoted',
  ACCEPTED: 'Approved',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  INVOICED: 'Invoiced',
  PAID: 'Paid',
  ARCHIVED: 'Archived',
  LOST: 'Lost',
}

/** `Deal.stage` (the database enum behind an Opportunity). */
export const DEAL_STAGE_LABELS: Record<string, string> = {
  LEAD: 'Lead',
  QUALIFIED: 'Qualified',
  PROPOSAL: 'Quoted',
  NEGOTIATION: 'Quoted',
  WON: 'Won',
  LOST: 'Lost',
}

// ─── documents ──────────────────────────────────────────────────────────────

export const QUOTE_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  ACCEPTED: 'Approved',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
}

export const JOB_STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
}

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  PAID: 'Paid',
  PARTIALLY_PAID: 'Partly paid',
  OVERDUE: 'Overdue',
  VOIDED: 'Voided',
  WRITTEN_OFF: 'Written off',
  CANCELLED: 'Cancelled',
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  STRIPE: 'Card',
  CARD: 'Card',
  ACH: 'Bank (ACH)',
  CHECK: 'Check',
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
  OTHER: 'Other',
}

export const PAYMENT_TERM_LABELS: Record<string, string> = {
  due_on_receipt: 'Due on receipt',
  due_on_completion: 'Due on completion',
  net_15: 'Net 15',
  net_30: 'Net 30',
  net_45: 'Net 45',
  net_60: 'Net 60',
}

// ─── calendar & comms ───────────────────────────────────────────────────────

export const BOOKING_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  CANCELLED: 'Cancelled',
  NO_SHOW: 'No-show',
  COMPLETED: 'Completed',
}

export const MESSAGE_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  QUEUED: 'Sending',
  SENDING: 'Sending',
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  FAILED: 'Not delivered',
  UNDELIVERED: 'Not delivered',
  OPENED: 'Opened',
  RECEIVED: 'Received',
}

export const CHANNEL_LABELS: Record<string, string> = {
  SMS: 'Text',
  sms: 'Text',
  MMS: 'Text',
  EMAIL: 'Email',
  email: 'Email',
  VOICE: 'Call',
  voice: 'Call',
  CALL: 'Call',
  call: 'Call',
}

export const LEAD_SOURCE_LABELS: Record<string, string> = {
  VOICE: 'Phone call',
  SMS: 'Text',
  EMAIL: 'Email',
  FORM: 'Website form',
  MANUAL: 'Added by hand',
  OTHER: 'Other',
  manual: 'Added by hand',
  inbound_sms: 'Text',
  inbound_email: 'Email',
  inbound_voice: 'Phone call',
  web_form: 'Website form',
  import: 'Import',
}

export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  CALL: 'Call',
  EMAIL: 'Email',
  NOTE: 'Note',
  TASK: 'Task',
  MEETING: 'Meeting',
  QUOTE_SENT: 'Quote sent',
  QUOTE_ACCEPTED: 'Quote approved',
  JOB_COMPLETED: 'Job completed',
  INVOICE_SENT: 'Invoice sent',
  INVOICE_PAID: 'Invoice paid',
  PAYMENT_RECEIVED: 'Payment received',
}

export const CREW_ROLE_LABELS: Record<string, string> = {
  LEAD: 'Crew lead',
  MEMBER: 'Crew',
  OBSERVER: 'Watching',
}

export const CONNECTION_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Connected',
  INACTIVE: 'Not connected',
  ERROR: 'Needs attention',
}

// ─── lookup ─────────────────────────────────────────────────────────────────

const MAPS = {
  workStage: WORK_STAGE_LABELS,
  dealStage: DEAL_STAGE_LABELS,
  quote: QUOTE_STATUS_LABELS,
  job: JOB_STATUS_LABELS,
  invoice: INVOICE_STATUS_LABELS,
  paymentMethod: PAYMENT_METHOD_LABELS,
  paymentTerms: PAYMENT_TERM_LABELS,
  booking: BOOKING_STATUS_LABELS,
  message: MESSAGE_STATUS_LABELS,
  channel: CHANNEL_LABELS,
  leadSource: LEAD_SOURCE_LABELS,
  activity: ACTIVITY_TYPE_LABELS,
  crewRole: CREW_ROLE_LABELS,
  connection: CONNECTION_STATUS_LABELS,
  contactType: CONTACT_TYPE_LABELS,
  role: ROLE_LABELS,
} as const

export type LabelKind = keyof typeof MAPS

/**
 * "IN_PROGRESS" → "In progress", "awaitingVerification" → "Awaiting
 * verification", "net_30" → "Net 30". The fallback for anything without a map
 * entry — never shows the raw key.
 */
export function humanizeEnum(value: string | null | undefined): string {
  if (!value) return ''
  const words = String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-.]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\bsms\b/g, 'text')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The label for a system value of a given kind; never the raw enum. */
export function statusLabel(kind: LabelKind, value: string | null | undefined): string {
  if (!value) return ''
  const map = MAPS[kind] as Record<string, string>
  return map[value] ?? map[value.toUpperCase()] ?? humanizeEnum(value)
}
